/**
 * Manual ROS bug reporter — header control next to the notification bell.
 * Opens only when the user clicks the bug icon (or "Report this error" after a crash).
 * Session console + API traffic are attached as ros-incident.txt on submit.
 * ITMS outages do not auto-open this dialog and are shown as a soft status, not a hard error.
 */
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"
import { Bug, X } from "@phosphor-icons/react"
import { useAuth } from "../contexts/AuthContext"
import HelpdeskUnavailable, {
  isHelpdeskUnavailable,
  parseHelpdeskDetail,
  readHelpdeskError,
} from "./HelpdeskUnavailable"
import { formatDiagnostics, latestTitle } from "../utils/errorCollector"

export function IncidentReportDialog({
  open,
  onClose,
  crashError,
  componentStack,
}) {
  const navigate = useNavigate()
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [impact, setImpact] = useState("medium")
  const [urgency, setUrgency] = useState("medium")
  const [diagnostics, setDiagnostics] = useState("")
  const [categoryLabel, setCategoryLabel] = useState("Internal app / ROS")
  const [unavailable, setUnavailable] = useState(null)
  const [formError, setFormError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showLog, setShowLog] = useState(false)

  const loadDefaults = () => {
    setUnavailable(null)
    setFormError(null)
    let cancelled = false
    fetch("/api/helpdesk/incident-defaults", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          const err = await readHelpdeskError(res, "Could not load the Internal app category.")
          if (isHelpdeskUnavailable(err)) {
            if (!cancelled) setUnavailable(err)
            return null
          }
          throw err
        }
        return res.json()
      })
      .then((body) => {
        if (cancelled || !body) return
        if (body?.categoryLabel) setCategoryLabel(body.categoryLabel)
      })
      .catch((err) => {
        if (cancelled) return
        if (isHelpdeskUnavailable(err)) {
          setUnavailable({
            message: err.message || "Could not reach Helpdesk.",
            status: err.status,
          })
          return
        }
        setFormError(err.message || "Could not reach Helpdesk. Try again in a moment.")
      })
    return () => { cancelled = true }
  }

  useEffect(() => {
    if (!open) return undefined
    setDescription("")
    setImpact("medium")
    setUrgency("medium")
    setShowLog(false)
    setFormError(null)
    setUnavailable(null)
    const extra = crashError
      ? `React crash: ${crashError.message || crashError}\n${crashError.stack || ""}\n${componentStack || ""}`
      : ""
    setTitle(crashError ? (crashError.message || latestTitle()).slice(0, 200) : latestTitle())
    setDiagnostics(formatDiagnostics(extra))
    const cancelDefaults = loadDefaults()
    return () => {
      cancelDefaults()
    }
  }, [open, crashError, componentStack])

  if (!open) return null

  const submit = async (e) => {
    e.preventDefault()
    setFormError(null)
    if (unavailable) return
    if (!title.trim() || !description.trim()) {
      setFormError("Add a title and describe what you were doing.")
      return
    }
    setLoading(true)
    try {
      const res = await fetch("/api/helpdesk/incident-report", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          impact,
          urgency,
          diagnostics,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const err = {
          message: parseHelpdeskDetail(data, `Could not submit (${res.status})`),
          status: res.status,
        }
        if (isHelpdeskUnavailable(err)) {
          setUnavailable(err)
          return
        }
        throw err
      }
      onClose()
      if (data.id) {
        if (crashError) {
          const base = import.meta.env.BASE_URL.replace(/\/$/, "")
          window.location.assign(`${base}/helpdesk/${data.id}`)
        } else {
          navigate(`/helpdesk/${data.id}`)
        }
      }
    } catch (err) {
      if (isHelpdeskUnavailable(err)) {
        setUnavailable({
          message: err.message || String(err),
          status: err.status,
        })
      } else {
        setFormError(err.message || String(err))
      }
    } finally {
      setLoading(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-black/40">
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
      <form
        onSubmit={submit}
        className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-lg my-4 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto p-6"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-lg font-semibold text-neutral-800 dark:text-white">Report a ROS problem</h2>
            <p className="text-xs text-neutral-500 mt-1">
              Creates an IT incident in <strong>{categoryLabel}</strong>. A text log of this page,
              recent API calls, and console output is attached as <code className="text-[10px]">ros-incident.txt</code>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-neutral-400 hover:text-neutral-700 dark:hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {unavailable ? (
          <HelpdeskUnavailable compact error={unavailable} onRetry={loadDefaults} />
        ) : formError ? (
          <div className="mb-3 text-sm text-red-600">{formError}</div>
        ) : null}

        <label className="block text-xs font-medium text-neutral-500 mb-3">
          Title
          <input
            className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-neutral-300 dark:border-white/20 bg-white dark:bg-slate-800"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            required
            disabled={Boolean(unavailable)}
          />
        </label>

        <label className="block text-xs font-medium text-neutral-500 mb-3">
          What were you doing?
          <textarea
            className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-neutral-300 dark:border-white/20 bg-white dark:bg-slate-800 min-h-[96px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="I clicked … and then …"
            required
            disabled={Boolean(unavailable)}
          />
        </label>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="text-xs font-medium text-neutral-500">
            Impact
            <select
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-neutral-300 dark:border-white/20 bg-white dark:bg-slate-800"
              value={impact}
              onChange={(e) => setImpact(e.target.value)}
              disabled={Boolean(unavailable)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
          <label className="text-xs font-medium text-neutral-500">
            Urgency
            <select
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-neutral-300 dark:border-white/20 bg-white dark:bg-slate-800"
              value={urgency}
              onChange={(e) => setUrgency(e.target.value)}
              disabled={Boolean(unavailable)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
        </div>

        <button
          type="button"
          onClick={() => setShowLog((v) => !v)}
          className="text-xs text-blue-600 hover:text-blue-800 mb-2"
        >
          {showLog ? "Hide collected log" : "Show collected log"}
        </button>
        {showLog && (
          <pre className="mb-3 text-[11px] bg-neutral-50 dark:bg-slate-800 border border-neutral-200 dark:border-white/10 rounded-lg p-3 max-h-48 overflow-auto whitespace-pre-wrap">
            {diagnostics || "(collecting…)"}
          </pre>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-lg border border-neutral-300 dark:border-white/20"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || Boolean(unavailable)}
            className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? "Submitting…" : "Submit incident"}
          </button>
        </div>
      </form>
      </div>
    </div>,
    document.body,
  )
}

export function IncidentCrashBar({ error, componentStack }) {
  const { user, hasPermission } = useAuth()
  const [open, setOpen] = useState(false)

  if (!user || !hasPermission("helpdesk.submit") || !error) return null

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-0 px-4 py-2 bg-neutral-900 text-white rounded-md text-sm"
        >
          Report this error
        </button>
      )}
      <IncidentReportDialog
        open={open}
        onClose={() => setOpen(false)}
        crashError={error}
        componentStack={componentStack}
      />
    </>
  )
}

export default function IncidentReporter() {
  const { user, hasPermission } = useAuth()
  const [open, setOpen] = useState(false)

  if (!user || !hasPermission("helpdesk.submit")) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative p-1.5 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 dark:focus:ring-offset-neutral-bg2 text-neutral-500 hover:text-brand-navy dark:text-text-muted dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10"
        title="Report a ROS problem"
        aria-label="Report a ROS problem"
      >
        <Bug size={20} weight="regular" />
      </button>
      <IncidentReportDialog open={open} onClose={() => setOpen(false)} />
    </>
  )
}
